/**
 * THE FOUR QUESTIONS THAT EARN A PRIOR ITS CREDIBILITY.
 *
 * CORE-E Stage B. Shown only for the categories `planCategoryBackgroundPrompts`
 * selected — an advanced claim or a named specialty — so a manager answers four
 * questions a handful of times, not seventy-two.
 *
 * There is NO strength field. The manager supplies facts and the server derives
 * what they are worth; the number that comes back is shown as a tier and a
 * sentence about how long their estimate holds, because "prior strength 30" is
 * an invitation to argue about a value that is not theirs to set.
 *
 * When the category already has demonstrated evidence, a derived change is NOT
 * applied — updating somebody's résumé must not rewrite the score that decides
 * who gets the next job. The panel says so and offers the deliberate act
 * separately, with the before and after visible.
 */
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Info } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  backgroundTierLabel,
  describeStrengthInJobs,
} from '@/lib/proficiency/categoryBackgroundPlan';
import {
  useSetCategoryBackground,
  useApplyCategoryBackgroundStrength,
  type CategoryBackgroundRow,
} from '@/hooks/proficiency/useCategoryBackground';

export interface CategoryBackgroundFieldsProps {
  shopId: string;
  technicianId: string;
  categoryKey: string;
  existing?: CategoryBackgroundRow;
}

const num = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export const CategoryBackgroundFields = ({
  shopId,
  technicianId,
  categoryKey,
  existing,
}: CategoryBackgroundFieldsProps) => {
  const [years, setYears] = useState(
    existing?.yearsInCategory != null ? String(existing.yearsInCategory) : '',
  );
  const [specialty, setSpecialty] = useState(existing?.isRegularSpecialty ?? false);
  const [leadYears, setLeadYears] = useState(
    existing?.leadRoleYears != null ? String(existing.leadRoleYears) : '',
  );
  const [credential, setCredential] = useState(existing?.credentialAsserted ?? false);
  const save = useSetCategoryBackground(shopId, technicianId);
  const apply = useApplyCategoryBackgroundStrength(shopId, technicianId);

  // SERVER STATE DECIDES WHAT IS SHOWN.
  //
  // The confirmation panel used to be driven by `save.data.requires_confirmation`,
  // which is a fact about a past request and never stops being true — so an
  // already-applied proposal stayed armed and re-clickable, and a reload
  // disagreed with the screen. `pending_confirmation` is persisted by the server
  // and cleared by the apply path, so reading it converges.
  //
  // The mutation result is used ONLY to cover the moment between a successful
  // save and the refetch landing, and `save.reset()` after a successful apply
  // makes even that transient state let go.
  const row = existing;
  const strength = row?.derivedPriorStrength ?? save.data?.derived_prior_strength ?? null;
  const revision = row?.proposalRevision ?? save.data?.proposal_revision ?? null;
  const needsConfirm =
    (row?.pendingConfirmation === true || save.data?.requires_confirmation === true)
    && strength !== null
    && revision !== null;
  const tier = backgroundTierLabel(strength);
  const reasons = row?.derivedReasons ?? save.data?.reasons ?? [];

  const onSave = () => {
    save.mutate(
      {
        categoryKey,
        facts: {
          yearsInCategory: num(years),
          isRegularSpecialty: specialty,
          leadRoleYears: num(leadYears),
          credentialAsserted: credential,
        },
      },
      {
        onSuccess: (r) => {
          if (r.requires_confirmation) {
            toast({
              title: 'Background saved — not yet applied',
              description:
                `${categoryKey} already has ${r.evidence_count} demonstrated outcome(s), so `
                + 'changing how much the opening estimate is trusted would move the '
                + 'scheduling score. Confirm below to apply it.',
            });
          } else {
            toast({
              title: 'Background saved',
              description: r.applied
                ? `${categoryKey} background recorded and applied.`
                : `${categoryKey} background recorded.`,
            });
          }
        },
        onError: (e) =>
          toast({ title: 'Could not save background', description: e.message, variant: 'destructive' }),
      },
    );
  };

  return (
    <div className="mt-2 rounded-md border border-dashed p-3 space-y-3" data-testid={`bg-fields-${categoryKey}`}>
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        Background for this category. This does not change the proficiency — it sets how
        long your estimate is trusted while OptiBay gathers evidence.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block font-medium">Years performing {categoryKey}</span>
          <Input
            type="number" min={0} max={70} value={years}
            onChange={(e) => setYears(e.target.value)}
            placeholder="e.g. 8"
            data-testid={`bg-years-${categoryKey}`}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">Years leading / diagnosing it</span>
          <Input
            type="number" min={0} max={70} value={leadYears}
            onChange={(e) => setLeadYears(e.target.value)}
            placeholder="0 if none"
            data-testid={`bg-lead-${categoryKey}`}
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={specialty}
            onCheckedChange={(v) => setSpecialty(v === true)}
            data-testid={`bg-specialty-${categoryKey}`}
          />
          Regularly performs this work
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={credential}
            onCheckedChange={(v) => setCredential(v === true)}
            data-testid={`bg-credential-${categoryKey}`}
          />
          Relevant credential (ASE / OEM)
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save background'}
        </Button>
        {tier && (
          <Badge variant="outline" data-testid={`bg-tier-${categoryKey}`}>
            {tier}
          </Badge>
        )}
      </div>

      {strength !== null && (
        <p className="text-xs text-muted-foreground" data-testid={`bg-meaning-${categoryKey}`}>
          {describeStrengthInJobs(strength)}
        </p>
      )}

      {reasons.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground" data-testid={`bg-reasons-${categoryKey}`}>
          {reasons.map((r) => (
            <li key={r.code}>
              {r.label} <span className="font-medium">+{r.points}</span>
            </li>
          ))}
        </ul>
      )}

      {needsConfirm && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/5 p-2" data-testid={`bg-confirm-${categoryKey}`}>
          <p className="flex items-start gap-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span>
              This category already has demonstrated work. Applying the new background
              will change the scheduling proficiency.
            </span>
          </p>
          <Button
            type="button" size="sm" variant="outline" className="mt-2"
            disabled={apply.isPending}
            onClick={() =>
              apply.mutate(
                { categoryKey, expectedStrength: strength!, expectedRevision: revision! },
                {
                  onSuccess: (r) => {
                    // Let go of the transient save result so the panel cannot
                    // survive on it; the refetched row now says pending=false.
                    save.reset();
                    toast({
                      title: 'Background applied',
                      description:
                        `${categoryKey} proficiency ${r.score_before ?? '—'} → ${r.score_after ?? '—'}.`,
                    });
                  },
                  onError: (e) =>
                    toast({ title: 'Could not apply', description: e.message, variant: 'destructive' }),
                },
              )
            }
          >
            {apply.isPending ? 'Applying…' : 'Apply and update proficiency'}
          </Button>
        </div>
      )}
    </div>
  );
};
